import cn from 'classnames';
import React, { useCallback } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import setSidebarActiveTabByNameOperation from 'editor/src/store/editorModules/sidebar/operation/setSidebarActiveTabByNameOperation';
import setSidebarActiveTabOperation from 'editor/src/store/editorModules/sidebar/operation/setSidebarActiveTabOperation';
import isSidebarTabActive from 'editor/src/store/editorModules/sidebar/selector/isSidebarTabActive';
import { TAB_NAMES } from 'editor/src/store/editorModules/sidebar/types';
import { getCurrentFontMetadataState } from 'editor/src/store/fonts/selector/getCurrentFontDefinition';
import { FontMetadataState } from 'editor/src/store/fonts/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import FontFamiliesMobileMenu from 'editor/src/component/BottomBarMobile/ActionBarSection/FontFamiliesMobileMenu';
import ControlButton from 'editor/src/component/ControlButton';
import IconArrowDown from 'editor/src/component/Icon/IconArrowDown';
import { CanShow, MenuItemProps } from 'editor/src/component/Menu/type';
import useMobileMenu from 'editor/src/component/useMobileMenu';
import WithTooltip from 'editor/src/component/WithTooltip';

import styles from './index.module.scss';

export const canShow: CanShow = (state, { isPersonalizationLocked }) => !isPersonalizationLocked;

function getButtonText(fontMetadataState: FontMetadataState, t: TFunction): string {
  if (!fontMetadataState.isShared) {
    return t('Multiple selected');
  }

  const { metadata } = fontMetadataState;
  const parts = [metadata.name];

  if (metadata.weight && metadata.weight !== 'Regular') {
    parts.push(metadata.weight);
  }

  if (metadata.style && metadata.style !== 'Normal') {
    parts.push(metadata.style);
  }

  return parts.join(' ');
}

type Props = Pick<MenuItemProps, 'isMobile' | 'close'>;

function ButtonFontFamily({ isMobile, close }: Props) {
  const { t } = useTranslation();
  const { fontMetadataState, isActive } = useSelector(
    (state) => ({
      fontMetadataState: getCurrentFontMetadataState(state),
      isActive: isSidebarTabActive(state, TAB_NAMES.FONT_FAMILIES),
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();
  const fontFamiliesMenu = useMobileMenu({ addBackdrop: false, forceHalfScreen: true });

  const handleClick = () => {
    if (isMobile) {
      fontFamiliesMenu.open();
      dispatch(setSidebarActiveTabByNameOperation(TAB_NAMES.FONT_FAMILIES));
    } else {
      dispatch(setSidebarActiveTabByNameOperation(TAB_NAMES.FONT_FAMILIES));
      close?.();
    }
  };

  const closeMenu = useCallback(() => {
    fontFamiliesMenu.close();
    dispatch(setSidebarActiveTabOperation(-1));
  }, []);

  return (
    <>
      <WithTooltip overlay={t('editor-font-family')}>
        <ControlButton
          onClick={handleClick}
          on={isActive}
          className={cn(styles.ButtonFontFamily, 'cy-button-font-family')}
        >
          <span>{getButtonText(fontMetadataState, t)}</span>
          <IconArrowDown />
        </ControlButton>
      </WithTooltip>
      {fontFamiliesMenu.render(<FontFamiliesMobileMenu closeMenuCb={closeMenu} />)}
    </>
  );
}

export default React.memo(ButtonFontFamily);
